import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { flatMap } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from '../../../../core/core.module';
import { IAppState } from '../../../../core/core.state';
import { IBudgetCalculateTotalResponse } from '../../../../core/models/budget-calculate-total-response.model';
import { IBudgetOptionView } from '../../../../core/models/budget-options-view.model';
import { IColumn, IColumnGroup } from '../../../../core/models/ly-table.model';
import { IOptionsRowCalculationResponse } from '../../../../core/models/options-row-calculation-response.model';
import { IPreviousOptionsResponse } from '../../../../core/models/previous-options-response.model';
import { IOption as TyBudget, IOption } from '../../../../core/models/ty-table.model';
import { IUserRole } from '../../../../core/models/user-roles.model';
import { selectUserDetails } from '../../../../core/store/store.selectors';
import { IErrorResponse } from '../../../../shared/models/error-response.model';
import { IFieldErrorResponse } from '../../../../shared/models/field-error-response.model';
import { IBudgetResponseModel } from '../../interfaces/budget-response.model';
import { IConfigProductHierarchyResponseModel } from '../../interfaces/config-product-hierarchy-response.model';
import { IMonthlyOptionColumn, IMonthlyOptionReport, IMonthlyOptionRow } from '../../interfaces/monthly-option-report.interface';
import { IOptionRequestModel } from '../../interfaces/option-request.model';
import { IOptionResponseModel } from '../../interfaces/option-response.model';
import { IOptionsApprovalRequestModel } from '../../interfaces/options-approval-request.model';
import { IOptionsApprovalResponseModel } from '../../interfaces/options-approval-response.model';
import { IOptionsAttributeValueResponseModel } from '../../interfaces/options-attribute-value-response.model';
import { IOptionsCalculateRowPayload } from '../../interfaces/options-calculate-row.interface';
import { ProductLevel } from '../../interfaces/product-level.enum';
import { ISubTimeOptionActualPercentageRequestModel } from '../../interfaces/sub-time-option-actual-percentage-request.model';
import { ISubTimeOptionResponseModel } from '../../interfaces/sub-time-option-response.model';
import { MapsCalculationService } from '../../services/maps-calculation.service';
import { MapsService } from '../../services/maps.service';
import { OptionsApprovalService } from '../../services/options-approval.service';
import { ProductHierarchyService } from '../../services/product-hierarchy.service';

const tyColumnGroups: Array<IColumnGroup> = [
    {
        name: 'options',
        order: 1,
        columns: ['sku_plan', 'op_sob']
    },
    {
        name: 'units',
        order: 2,
        columns: ['units_sales', 'units_sob']
    },
    {
        name: 'value',
        order: 3,
        columns: ['value_wsp_total', 'value_sob', 'value_budget', 'value_var']
    }
];
const tyColumns: Array<IColumn> = [
    {
        name: 'sku_plan',
        label: 'Style plan',
        order: 1,
        type: 'decimal'
    },
    {
        name: 'op_sob',
        label: 'SOB',
        order: 2,
        type: 'percentage'
    },
    {
        name: 'units_sales',
        label: 'Units Sales',
        order: 3,
        type: 'decimal'
    },
    {
        name: 'units_sob',
        label: 'Units SOB',
        order: 4,
        type: 'percentage'
    },
    {
        name: 'value_wsp_total',
        label: 'WSP Total',
        order: 5,
        type: 'decimal'
    },
    {
        name: 'value_sob',
        label: 'Value SOB',
        order: 6,
        type: 'percentage'
    },
    {
        name: 'value_budget',
        label: 'Value Budget',
        order: 7,
        type: 'decimal'
    },
    {
        name: 'value_var',
        label: 'Value Var',
        order: 8,
        type: 'decimal'
    }
];

const lyColumnGroups: Array<IColumnGroup> = [
    {
        name: 'options',
        order: 1,
        columns: ['op_sku_count', 'op_sob']
    },
    {
        name: 'units',
        order: 2,
        columns: ['units_sales', 'units_sob']
    },
    {
        name: 'value',
        order: 3,
        columns: ['value_wsp_total', 'value_sob']
    },
    {
        name: 'avg units + value',
        order: 4,
        columns: ['avg_units', 'moq', 'avg_wsp']
    }
];
const lyColumns: Array<IColumn> = [
    {
        name: 'product_level_value',
        label: '',
        order: 1,
        type: 'text'
    },
    {
        name: 'op_sku_count',
        label: 'Style count',
        order: 2,
        type: 'decimal'
    },
    {
        name: 'op_sob',
        label: 'SOB',
        order: 3,
        type: 'percentage'
    },
    {
        name: 'units_sales',
        label: 'Units Sales',
        order: 4,
        type: 'decimal'
    },
    {
        name: 'units_sob',
        label: 'Units SOB',
        order: 5,
        type: 'percentage'
    },
    {
        name: 'value_wsp_total',
        label: 'WSP Total',
        order: 6,
        type: 'decimal'
    },
    {
        name: 'value_sob',
        label: 'Value SOB',
        order: 7,
        type: 'percentage'
    },
    {
        name: 'avg_units',
        label: 'AVG Units',
        order: 8,
        type: 'decimal'
    },
    {
        name: 'moq',
        label: 'MOQ',
        order: 9,
        type: 'decimal'
    },
    {
        name: 'avg_wsp',
        label: 'AVG WSP',
        order: 10,
        type: 'decimal'
    }
];

@Component({
    selector: 'lynkd-pattern-plans',
    templateUrl: './plans.component.html',
    styleUrls: ['./plans.component.scss']
})
export class PlansComponent implements OnInit {
    public selectedView: string = 'budget';

    // public data: IBudgetOptionView;
    public availableBrandValues: Array<IConfigProductHierarchyResponseModel> = [];
    public availableTimeValues: Array<string>;
    public availableDepartmentValues: Array<IConfigProductHierarchyResponseModel> = [];
    public filteredDepartmentValues: Array<IConfigProductHierarchyResponseModel> = [];
    public availableSubDepartmentValues: Array<IConfigProductHierarchyResponseModel> = [];
    public filteredSubDepartmentValues: Array<IConfigProductHierarchyResponseModel> = [];
    public availableCategoryValues: Array<IConfigProductHierarchyResponseModel> = [];
    public filteredCategoryValues: Array<IConfigProductHierarchyResponseModel> = [];
    public selectedTimeValue: string;
    public selectedBrandId: string;
    public selectedDepartmentId: string;
    public selectedSubDepartmentId: string;
    public selectedCategoryId: string;
    public hideZeros: boolean;
    public selectedOptionsView: IBudgetOptionView;
    public monthlyOptionsData: IMonthlyOptionReport;
    public tyTableState: Array<IOption>;
    public loading: boolean = false;
    public tYOptionsApprovalData: IOptionsApprovalResponseModel;
    public lYOptionsApprovalData: IOptionsApprovalResponseModel;
    public filteredAttributeValues: Array<IOptionsAttributeValueResponseModel> = [];
    public _activeRoles: Array<string> = [];

    public constructor(
        private readonly _mapsService: MapsService,
        private readonly _store: Store<IAppState>,
        private readonly _snackbarService: MatSnackBar,
        private readonly _optionsApprovalService: OptionsApprovalService,
        private readonly _notificationService: NotificationService,
        private readonly _mapsCalculationService: MapsCalculationService,
        private readonly _productHierarchyService: ProductHierarchyService
    ) {}

    public hasRole(role: 'manager' | 'planner'): boolean {
        if (this._activeRoles.length === 0) {
            return false;
        }
        return this._activeRoles.includes(role);
    }

    public async ngOnInit(): Promise<void> {
        this.loading = true;

        try {
            // Load all product hierarchy values
            this.availableBrandValues = await this._productHierarchyService.getAttributeValues(ProductLevel.Brand);
            this.availableTimeValues = await this._mapsService.getTimeValues();
            this.availableDepartmentValues = await this._productHierarchyService.getAttributeValues(ProductLevel.Department);
            this.availableSubDepartmentValues = await this._productHierarchyService.getAttributeValues(ProductLevel.SubDepartment);
            this.availableCategoryValues = await this._productHierarchyService.getAttributeValues(ProductLevel.Category);

            // Get user roles
            const userDetails: { email: string; roles: Array<IUserRole> } = await firstValueFrom(
                this._store.select(selectUserDetails)
            );
            const mapsRole: IUserRole | undefined = userDetails?.roles.find((role: IUserRole): boolean => role.module === 'maps');
            this._activeRoles = mapsRole?.roles || [];

            if (this.availableBrandValues?.length > 0 && this.availableTimeValues?.length > 0) {
                // Set default selected values
                this.selectedTimeValue = this.availableTimeValues.length > 1 ? this.availableTimeValues[1] : null;
                this.selectedBrandId = this.availableBrandValues[0].id;

                // Initialize filtered departments based on selected brand
                const brandShortId: string = this.getShortId(this.selectedBrandId);
                this.filteredDepartmentValues = this.availableDepartmentValues.filter(
                    (department: IConfigProductHierarchyResponseModel): boolean => department.id.includes(brandShortId)
                );

                await this.filterChanged();
            }
        } catch (error: unknown) {
            const errorMessage: string = error instanceof Error ? error.message : 'An error occurred';
            this._snackbarService.open(errorMessage, 'close', {
                duration: 1500
            });
        } finally {
            this.loading = false;
        }
    }

    public async setBudgetAndOptionsData(attributeValue: string): Promise<void> {
        if (this.tYOptionsApprovalData && this.lYOptionsApprovalData) {
            let tyData: Array<TyBudget>;
            let tyTotal: TyBudget;
            const lastYearOptions: Array<IPreviousOptionsResponse> = [];
            const thisYearOptions: Array<IOptionsRowCalculationResponse> = [];

            // Process each attribute value from both years' data
            for (let i: number = 0; i < this.tYOptionsApprovalData.attributeValues.length; i++) {
                const tyOptionsAttributeValueData: IOptionsAttributeValueResponseModel = this.tYOptionsApprovalData.attributeValues[i];
                const lyOptionsAttributeValueData: IOptionsAttributeValueResponseModel = this.lYOptionsApprovalData.attributeValues[i];

                if (tyOptionsAttributeValueData && lyOptionsAttributeValueData) {
                    // Map and filter previous year options
                    const mappedPrevOptions: Array<IPreviousOptionsResponse> = this.mapOptionToPreviousOptions(
                        lyOptionsAttributeValueData.attributeValue,
                        lyOptionsAttributeValueData.id,
                        lyOptionsAttributeValueData.options
                    ).filter((option: IPreviousOptionsResponse): boolean =>
                        !['total', 'grand total'].includes(option.product_level_value.toLowerCase())
                    );

                    // Map and filter current year options
                    const mappedCurrentOptions: Array<IOptionsRowCalculationResponse> = this.mapOptionToOptionsRowCalculation(
                        tyOptionsAttributeValueData.attributeValue,
                        tyOptionsAttributeValueData.id,
                        tyOptionsAttributeValueData.options
                    ).filter((option: IOptionsRowCalculationResponse): boolean =>
                        option.product_level_value !== 'Total'
                    );

                    lastYearOptions.push(...mappedPrevOptions);
                    thisYearOptions.push(...mappedCurrentOptions);
                }
            }

            // Process options based on data availability
            if (thisYearOptions.length > 0 && lastYearOptions.length > 0) {
                // Get existing metrics to avoid duplicates
                const metricsInList: Array<string> = thisYearOptions.map(
                    (option: IOptionsRowCalculationResponse): string => option.product_level_value
                );

                // Create options for metrics that exist in LY but not in TY
                const newOptions: Array<IOptionsRowCalculationResponse> = lastYearOptions
                    .filter((option: IPreviousOptionsResponse): boolean => !metricsInList.includes(option.product_level_value))
                    .map((option: IPreviousOptionsResponse): IOptionsRowCalculationResponse => ({
                        attributeValueId: option.attributeValueId,
                        optionId: option.optionId,
                        attribute_value: option.attribute_value,
                        product_level_value: option.product_level_value,
                        product_level_name: option.product_level_name,
                        sku_plan: 0,
                        op_sob: 0,
                        units_sales: 0,
                        units_sob: 0,
                        value_wsp_total: 0,
                        value_sob: 0,
                        value_budget: 0,
                        value_var: 0
                    }));

                // Combine current year options with new options
                tyData = [...thisYearOptions, ...newOptions];

                // Calculate totals from this year's options
                tyTotal = {
                    attributeValueId: null,
                    optionId: null,
                    attribute_value: attributeValue,
                    product_level_value: 'Total',
                    sku_plan: this.getCalculationResponseColumnTotal('sku_plan', thisYearOptions),
                    op_sob: this.getCalculationResponseColumnTotal('op_sob', thisYearOptions),
                    units_sales: this.getCalculationResponseColumnTotal('units_sales', thisYearOptions),
                    units_sob: this.getCalculationResponseColumnTotal('units_sob', thisYearOptions),
                    value_wsp_total: this.getCalculationResponseColumnTotal('value_wsp_total', thisYearOptions),
                    value_sob: this.getCalculationResponseColumnTotal('value_sob', thisYearOptions),
                    value_budget: this.getCalculationResponseColumnTotal('value_budget', thisYearOptions),
                    value_var: this.getCalculationResponseColumnTotal('value_var', thisYearOptions)
                };
            } else {
                // If no current year data, create empty options from last year's data
                tyData = lastYearOptions.map((option: IPreviousOptionsResponse): IOptionsRowCalculationResponse => ({
                    attributeValueId: option.attributeValueId,
                    optionId: option.optionId,
                    attribute_value: option.attribute_value,
                    product_level_value: option.product_level_value,
                    product_level_name: option.product_level_name,
                    sku_plan: 0,
                    op_sob: 0,
                    units_sales: 0,
                    units_sob: 0,
                    value_wsp_total: 0,
                    value_sob: 0,
                    value_budget: 0,
                    value_var: 0
                }));

                // Create empty totals
                tyTotal = {
                    attributeValueId: null,
                    optionId: null,
                    attribute_value: attributeValue,
                    product_level_value: 'Total',
                    sku_plan: 0,
                    op_sob: 0,
                    units_sales: 0,
                    units_sob: 0,
                    value_wsp_total: 0,
                    value_sob: 0,
                    value_budget: 0,
                    value_var: 0
                };
            }

            // Filter data based on selected category and zeros
            let filteredData: Array<IOption> = tyData;
            let lastYearsFilteredData: Array<IPreviousOptionsResponse> = lastYearOptions;

            if (this.selectedCategoryId) {
                filteredData = tyData.filter((option: IOption): boolean =>
                    option.product_level_value.includes(this.selectedCategoryId)
                );
                lastYearsFilteredData = lastYearOptions.filter((option: IPreviousOptionsResponse): boolean =>
                    option.product_level_value.includes(this.selectedCategoryId)
                );
            }

            if (this.hideZeros) {
                filteredData = filteredData.filter((option: IOption): boolean => option.sku_plan > 0);
                lastYearsFilteredData = lastYearsFilteredData.filter((option: IPreviousOptionsResponse): boolean =>
                    option.op_sku_count > 0
                );
            }

            // Update the view model
            this.selectedOptionsView = {
                attribute_value: attributeValue,
                ty_table_data: {
                    columnGroups: tyColumnGroups,
                    columns: tyColumns,
                    data: tyData,
                    filtered_data: filteredData,
                    total: tyTotal
                },
                ly_table_data: {
                    columnGroups: lyColumnGroups,
                    columns: lyColumns,
                    data: lastYearOptions,
                    filtered_data: lastYearsFilteredData,
                    totals: {
                        attribute_value: attributeValue,
                        product_level_value: 'Total',
                        op_sku_count: this.getPreviousOptionsResponseColumnTotal('op_sku_count', lastYearsFilteredData),
                        op_sob: this.getPreviousOptionsResponseColumnTotal('op_sob', lastYearsFilteredData),
                        units_sales: this.getPreviousOptionsResponseColumnTotal('units_sales', lastYearsFilteredData),
                        units_sob: this.getPreviousOptionsResponseColumnTotal('units_sob', lastYearsFilteredData),
                        value_wsp_total: this.getPreviousOptionsResponseColumnTotal('value_wsp_total', lastYearsFilteredData),
                        value_sob: this.getPreviousOptionsResponseColumnTotal('value_sob', lastYearsFilteredData),
                        avg_units: this.getPreviousOptionsResponseColumnTotal('avg_units', lastYearsFilteredData),
                        moq: this.getPreviousOptionsResponseColumnTotal('moq', lastYearsFilteredData),
                        avg_wsp: this.getPreviousOptionsResponseColumnTotal('avg_wsp', lastYearsFilteredData)
                    }
                }
            };
        }
    }

    public getCalculationResponseColumnTotal(columnName: string, rows: Array<IOptionsRowCalculationResponse>): number {
        let total: number = 0;

        for (const row of rows) {
            if (row[columnName]) {
                total += row[columnName];
            }
        }

        return total;
    }

    public getPreviousOptionsResponseColumnTotal(
        columnName: Exclude<keyof IPreviousOptionsResponse, 'attribute_value' | 'product_level_value' | 'product_level_name'>,
        rows: Array<IPreviousOptionsResponse>
    ): number {
        let total: number = 0;

        if (columnName === 'avg_units' || columnName === 'avg_wsp') {
            for (const row of rows) {
                if (row[columnName]) {
                    total += row[columnName];
                }
            }
            total = total / rows.length;
        } else {
            for (const row of rows) {
                if (row[columnName]) {
                    total += row[columnName];
                }
            }
        }

        return total;
    }

    public mapOptionToPreviousOptions(
        attributeValue: string,
        attributeValueId: number,
        options: Array<IOptionResponseModel>
    ): Array<IPreviousOptionsResponse> {
        return options.map((option: IOptionResponseModel): IPreviousOptionsResponse => ({
            attributeValueId,
            optionId: option.id,
            attribute_value: attributeValue,
            product_level_value: option.productLevelId,
            product_level_name: option.productLevelName,
            op_sku_count: option.optionsCount || 0,
            op_sob: option.optionShareOfBusiness || 0,
            units_sales: option.unitsSales || 0,
            units_sob: option.unitsShareOfBusiness || 0,
            value_wsp_total: option.valueWholesalePriceTotal || 0,
            value_sob: option.valueShareOfBusiness || 0,
            avg_units: option.averageUnits || 0,
            moq: option.minimumOrderQuantity || 0,
            avg_wsp: option.averageWholesalePrice || 0
        }));
    }

    public mapOptionToOptionsRowCalculation(
        attributeValue: string,
        attributeValueId: number,
        options: Array<IOptionResponseModel>
    ): Array<IOptionsRowCalculationResponse> {
        return options.map((option: IOptionResponseModel): IOptionsRowCalculationResponse => ({
            attributeValueId,
            optionId: option.id,
            attribute_value: attributeValue,
            product_level_value: option.productLevelId,
            product_level_name: option.productLevelName,
            sku_plan: option.optionsCount || 0,
            op_sob: option.optionShareOfBusiness || 0,
            units_sales: option.unitsSales || 0,
            units_sob: option.unitsShareOfBusiness || 0,
            value_wsp_total: option.valueWholesalePriceTotal || 0,
            value_sob: option.valueShareOfBusiness || 0,
            value_budget: option.valueBudget || 0,
            value_var: option.valueVariance || 0
        }));
    }

    public mapOptionsToMonthlyOptionReport(
        attributeValue: string,
        attributeValueModel: IOptionsAttributeValueResponseModel
    ): IMonthlyOptionReport {
        const options: Array<IOptionResponseModel> = attributeValueModel.options;
        const budgets: Array<IBudgetResponseModel> = attributeValueModel.budgets;

        const rows: Array<IMonthlyOptionRow> = options.map((option: IOptionResponseModel): IMonthlyOptionRow => ({
            attributeValueId: attributeValueModel.id,
            attributeValue: attributeValueModel.attributeValue,
            optionId: option.id,
            product_level_value: option.productLevelId,
            product_level_name: option.productLevelName,
            sku_plans: {
                ...option.subTimeOptions.reduce((acc: Record<string, number>, subTimeOption: ISubTimeOptionResponseModel) => {
                    acc[subTimeOption.configSubTimeValue.displayName] = subTimeOption.optionsCount;
                    return acc;
                }, {} as Record<string, number>),
                ...{ sku_plan: option.optionsCount, Total: 0 }
            }
        }));
        const firstOption: IOptionResponseModel = options[0];
        const monthlyPercentage: Array<IMonthlyOptionColumn> = firstOption.subTimeOptions.map(
            (subTimeOption: ISubTimeOptionResponseModel): IMonthlyOptionColumn => ({
                id: subTimeOption.configSubTimeValue.id,
                column_name: subTimeOption.configSubTimeValue.displayName,
                flow_perc: subTimeOption.configSubTimeValue.flowPercentage,
                actual_perc: 0,
                budget: 0
            })
        );

        const filteredRows: Array<IMonthlyOptionRow> = this.filterMonthlyOptions(rows);
        this.setInitialMonthlyValues(monthlyPercentage,filteredRows);

        const columns: Array<IMonthlyOptionColumn> = firstOption.subTimeOptions.map(
            (subTimeOption: ISubTimeOptionResponseModel): IMonthlyOptionColumn =>
                this.buildMonthlyColumn(options, budgets, subTimeOption, filteredRows)
        );
        return {
            columns,
            rows,
            filtered_rows: filteredRows,
            total: 0, // TODO: Is this the sum of the totals of all options?
            attribute_value: attributeValue
        };
    }

    public calculateMonthlyOptionColumns(
        attributeValueModels: Array<IOptionsAttributeValueResponseModel>,
        monthlyRows: Array<IMonthlyOptionRow>
    ): Array<IMonthlyOptionColumn> {
        const allCurrentOptions: Array<IOptionResponseModel> = flatMap(
            attributeValueModels,
            (currentAttributeValue: IOptionsAttributeValueResponseModel) => currentAttributeValue.options
        ).filter((option: IOptionResponseModel) => option.productLevelId !== 'Total');

        const allCurrentBudgets: Array<IBudgetResponseModel> = flatMap(
            attributeValueModels,
            (currentAttributeValue: IOptionsAttributeValueResponseModel) => currentAttributeValue.budgets
        ).filter((option: IBudgetResponseModel) => option.productLevelId !== 'Total');

        return allCurrentOptions[0].subTimeOptions.map((subTimeOption: ISubTimeOptionResponseModel): IMonthlyOptionColumn =>
            this.buildMonthlyColumn(allCurrentOptions, allCurrentBudgets, subTimeOption, monthlyRows)
        );
    }

    public buildMonthlyColumn(
        options: Array<IOptionResponseModel>,
        budgets: Array<IBudgetResponseModel>,
        subTimeOption: ISubTimeOptionResponseModel,
        monthlyRows: Array<IMonthlyOptionRow>
    ): IMonthlyOptionColumn {
        const subTimeOptions: Array<ISubTimeOptionActualPercentageRequestModel> = flatMap(options, (t: IOptionResponseModel) => {
            const budget: IBudgetResponseModel = budgets.find((x: IBudgetResponseModel) => x.productLevelId === t.productLevelId);
            return t.subTimeOptions.map((mappedSubTimeOption: ISubTimeOptionResponseModel): ISubTimeOptionActualPercentageRequestModel => {
                const filterRow: IMonthlyOptionRow | undefined = monthlyRows.find(
                    (row: IMonthlyOptionRow) => row.optionId === t.id
                );
                const columnCount: number | undefined = filterRow
                    ? filterRow.sku_plans[mappedSubTimeOption.configSubTimeValue.displayName]
                    : undefined;
                const optionsCount: number = columnCount ?? t.optionsCount;
                const optionSummary: ISubTimeOptionActualPercentageRequestModel = {
                    ...mappedSubTimeOption,
                    optionsCount,
                    parentOptionCount: t.optionsCount,
                    budget: budget ? budget.totalBudget * (budget.fashionPercentage / 100) : 0
                };
                return optionSummary;
            });
        }).filter(
            (option: ISubTimeOptionActualPercentageRequestModel) =>
                option.configSubTimeValue.id === subTimeOption.configSubTimeValue.id
        );

        return {
            id: subTimeOption.configSubTimeValue.id,
            column_name: subTimeOption.configSubTimeValue.displayName,
            flow_perc: subTimeOption.configSubTimeValue.flowPercentage,
            actual_perc: this._mapsCalculationService.getSubTimeOptionActualPercentage(subTimeOptions),
            budget: this._mapsCalculationService.getSubTimeOptionBudget(subTimeOptions)
        };
    }

    public async setAllMonthlyOptions(attributeValue: string): Promise<void> {
        if (this.tYOptionsApprovalData) {
            // Reset monthlyOptionsData
            this.monthlyOptionsData = {
                columns: [],
                attribute_value: attributeValue,
                rows: [],
                filtered_rows: [],
                total: 0
            };

            for (const tyOptionsAttributeValueData of this.tYOptionsApprovalData.attributeValues) {
                if (tyOptionsAttributeValueData) {
                    const mappedOptionsToMonthlyOptionReport: IMonthlyOptionReport = this.mapOptionsToMonthlyOptionReport(
                        tyOptionsAttributeValueData.attributeValue,
                        tyOptionsAttributeValueData
                    );

                    const rows: Array<IMonthlyOptionRow> = [
                        ...(this.monthlyOptionsData ? this.monthlyOptionsData.rows : []),
                        ...mappedOptionsToMonthlyOptionReport.rows
                    ];
                    const filteredRows: Array<IMonthlyOptionRow> = this.filterMonthlyOptions(rows);

                    this.monthlyOptionsData = {
                        columns: mappedOptionsToMonthlyOptionReport.columns,
                        attribute_value: attributeValue,
                        rows,
                        filtered_rows: filteredRows,
                        total: 0
                    };
                }
            }

            if (this.selectedBrandId === 'ALL') {
                const firstOption: IOptionResponseModel = this.tYOptionsApprovalData.attributeValues[0].options[0];
                const monthlyPercentage: Array<IMonthlyOptionColumn> = firstOption.subTimeOptions.map(
                    (subTimeOption: ISubTimeOptionResponseModel) => ({
                        id: subTimeOption.configSubTimeValue.id,
                        column_name: subTimeOption.configSubTimeValue.displayName,
                        flow_perc: subTimeOption.configSubTimeValue.flowPercentage,
                        actual_perc: 0,
                        budget: 0
                    })
                );
                this.setInitialMonthlyValues(monthlyPercentage, this.monthlyOptionsData.rows);
                this.monthlyOptionsData.columns = this.calculateMonthlyOptionColumns(
                    this.tYOptionsApprovalData.attributeValues,
                    this.monthlyOptionsData.rows
                );
            }

            if (this.monthlyOptionsData && this.monthlyOptionsData.rows) {
                const totalRow: IMonthlyOptionRow = {
                    attributeValueId: null,
                    attributeValue: null,
                    optionId: null,
                    product_level_value: 'Total',
                    product_level_name: 'Total',
                    sku_plans: Object.keys(this.monthlyOptionsData.rows[0].sku_plans).reduce(
                        (acc: Record<string, number>, key: string) => {
                            acc[key] = 0;
                            return acc;
                        },
                        {} as Record<string, number>
                    )
                };
                this.monthlyOptionsData.rows = [
                    // Add total to top of row list, and set all sku values to 0
                    totalRow,
                    ...this.monthlyOptionsData.rows
                ];
                this.monthlyOptionsData.filtered_rows = [
                    // Add total to top of row list, and set all sku values to 0
                    totalRow,
                    ...this.monthlyOptionsData.filtered_rows
                ];
            }
        }
    }

    public setInitialMonthlyValues(monthlyPercentage: Array<IMonthlyOptionColumn>, rows: Array<IMonthlyOptionRow>): void {
        for (const row of rows) {
            if (
                row.product_level_value.toLowerCase() !== 'total' &&
                row.product_level_value.toLowerCase() !== 'sku_plan' &&
                this._mapsCalculationService.allSkusAreZero(row)
            ) {
                const calculatedRow: IMonthlyOptionRow = this._mapsCalculationService.calculateAndTruncateRowSkus(
                    row,
                    monthlyPercentage
                );
                if (calculatedRow) {
                    // Edit the row sku_plans directly so that the changes made to the data variable
                    // reflects in the parent component that passed the data variable to this child component
                    Object.keys(calculatedRow.sku_plans).forEach((key: string) => {
                        row.sku_plans[key] = calculatedRow.sku_plans[key];
                    });
                }
            }
        }
    }

    public filterMonthlyOptions(rows: Array<IMonthlyOptionRow>): Array<IMonthlyOptionRow> {
        let filteredRows: Array<IMonthlyOptionRow>;
        if (this.selectedCategoryId) {
            filteredRows = rows.filter((row: IMonthlyOptionRow): boolean =>
                row.product_level_value.includes(this.selectedCategoryId)
            );
        } else {
            filteredRows = rows;
        }
        if (this.hideZeros) {
            filteredRows = filteredRows.filter((row: IMonthlyOptionRow): boolean =>
                row.sku_plans.sku_plan > 0
            );
        }
        return filteredRows;
    }

    public async departmentChanged(): Promise<void> {
        const departmentShortId: string = this.getShortId(this.selectedDepartmentId);
        this.filteredSubDepartmentValues = this.availableSubDepartmentValues.filter((department: IConfigProductHierarchyResponseModel): boolean =>
            department.id.includes(departmentShortId)
        );
        this.selectedSubDepartmentId = '';
        this.selectedCategoryId = '';
        await this.filterChanged();
    }

    public async subDepartmentChanged(): Promise<void> {
        const subDepartmentShortId: string = this.getShortId(this.selectedSubDepartmentId);
        this.filteredCategoryValues = this.availableCategoryValues.filter((category: IConfigProductHierarchyResponseModel): boolean =>
            category.id.includes(subDepartmentShortId)
        );
        this.selectedCategoryId = '';
        await this.filterChanged();
    }

    public async categoryChanged(): Promise<void> {
        await this.setBudgetAndOptionsData(this.selectedBrandId);
        await this.setAllMonthlyOptions(this.selectedBrandId);
    }

    public async hideZerosChanged(): Promise<void> {
        await this.setBudgetAndOptionsData(this.selectedBrandId);
        await this.setAllMonthlyOptions(this.selectedBrandId);
        this.filterBudgets();
    }

    public filterBudgets(): void {
        this.filteredAttributeValues = this.tYOptionsApprovalData.attributeValues;
        if (this.hideZeros) {
            this.filteredAttributeValues = [];
            for (const attributeValue of this.tYOptionsApprovalData.attributeValues) {
                this.filteredAttributeValues.push({
                    ...attributeValue,
                    budgets: attributeValue.budgets.filter((budget: IBudgetResponseModel): boolean =>
                        budget.totalBudget > 0 || budget.fashionPercentage > 0 || budget.corePercentage > 0
                    )
                });
            }
        }
    }

    public getShortId(id: string): string {
        if (!id){
            return id;
        }
        return this._optionsApprovalService.getShortId(id);
    }

    public async filterChanged(): Promise<void> {
        if (!(this.selectedTimeValue && this.selectedBrandId && this.selectedDepartmentId && this.selectedSubDepartmentId)) {
            return;
        }
        this.loading = true;
        try {
            await this.loadOptionsApprovalData(
                this.selectedTimeValue,
                this.selectedSubDepartmentId,
                this.selectedBrandId,
                this.selectedCategoryId
            );
            await this.setBudgetAndOptionsData(this.selectedBrandId);
            await this.setAllMonthlyOptions(this.selectedBrandId);
            this.filterBudgets();
            this.loading = false;
        } catch (e) {
            this.showErrorMessage(e);
        }
    }

    public showErrorMessage(response: HttpErrorResponse): void {
        const error: IErrorResponse = response.error as IErrorResponse;
        let message: string;
        if (error.fieldErrors && error.fieldErrors.length > 0) {
            message = error.fieldErrors.map((t: IFieldErrorResponse) => t.message).join('\n');
        } else {
            message = error.message;
        }
        this._notificationService.error(message);
    }

    public async loadOptionsApprovalData(
        timeValue: string,
        subDepartmentId: string,
        attributeValue: string,
        categoryId: string
    ): Promise<void> {
        this.loading = true;
        try {
            const tYTimeValue: string = timeValue;
            const lYTimeValue: string = await this._mapsService.getPreviousTimeValue(tYTimeValue);

            // Brand is now part of subDepartmentId, and categoryId is no longer needed
            this.lYOptionsApprovalData = await this._optionsApprovalService.getOptionsApproval(
                lYTimeValue,
                subDepartmentId
            );
            this.tYOptionsApprovalData = await this._optionsApprovalService.getOptionsApproval(
                tYTimeValue,
                subDepartmentId
            );
            this.loading = false;
        } catch (e) {
            this.loading = false;
            throw e;
        }
    }

    public async handleOptionsTableUpdate(budgetUpdates: {
        attributeValueId: number;
        budgetChange: Array<IBudgetCalculateTotalResponse>;
    }): Promise<void> {
        // Update the specific budget in the tYOptionsApprovalData
        if (this.tYOptionsApprovalData && this.lYOptionsApprovalData) {
            // We're working with a single attribute here, so filter out that attribute value data and pass
            // it to the rest of the function
            const tyOptionsAttributeValueData: IOptionsAttributeValueResponseModel =
                this.tYOptionsApprovalData.attributeValues.find(
                    (optionsAttributeValue: IOptionsAttributeValueResponseModel) =>
                        optionsAttributeValue.id === budgetUpdates.attributeValueId
                );

            if (tyOptionsAttributeValueData) {
                tyOptionsAttributeValueData.budgets = budgetUpdates.budgetChange.map((budget: IBudgetCalculateTotalResponse) => ({
                    id: budget?.id,
                    productLevelId: budget.productLevelId,
                    productLevelName: budget.productLevelName,
                    totalBudget: Number(budget.total_budget),
                    fashionPercentage: Number(budget.fashion_percentage),
                    corePercentage: Number(budget.core_percentage)
                }));

                // Trigger Yearly and Monthly Options table recalculate
                this.tYOptionsApprovalData = this._mapsCalculationService.calculateOptions(
                    this.selectedBrandId,
                    this.tYOptionsApprovalData,
                    this.lYOptionsApprovalData
                );
                await this.setBudgetAndOptionsData(this.selectedBrandId);
                await this.setAllMonthlyOptions(this.selectedBrandId);
            } else {
                this.monthlyOptionsData = null;
                this.selectedOptionsView = null;
            }
        } else {
            this.monthlyOptionsData = null;
            this.selectedOptionsView = null;
        }
    }

    public async approve(): Promise<void> {
        if (!(this.selectedTimeValue && this.selectedBrandId)) {
            this._snackbarService.open('Please select a season and an attribute', 'close');
            return;
        }

        if (!(this.selectedSubDepartmentId && this.selectedSubDepartmentId)) {
            this._snackbarService.open('Please select a department and sub department', 'close');
            return;
        }
        this.loading = true;

        try {
            await this._optionsApprovalService.approveOptionsApproval(this.tYOptionsApprovalData.id);
            this._snackbarService.open('Budget approved', 'close');
        } catch (e) {
            this.showErrorMessage(e);
        }

        this.loading = false;
    }

    public async save(): Promise<void> {
        if (!(this.selectedTimeValue && this.selectedBrandId)) {
            this._snackbarService.open('Please select a time value and an attribute value', 'close');
            return;
        }
        if (!(this.selectedSubDepartmentId && this.selectedSubDepartmentId)) {
            this._snackbarService.open('Please select a department and sub department', 'close');
            return;
        }

        const saveModel: IOptionsApprovalRequestModel = this.mapOptionsApprovalResponseToRequest(this.tYOptionsApprovalData);
        if (saveModel.attributeValues.length === 0) {
            this._snackbarService.open('Please complete the budget for atleast one attribute value', 'close');
            return;
        }

        this.loading = true;

        try {
            await this._optionsApprovalService.saveOptionsApproval(saveModel);
            this._snackbarService.open('Options and budget saved', 'close');
        } catch (e) {
            this.showErrorMessage(e);
        }
        this.loading = false;
    }

    public mapOptionsApprovalResponseToRequest(
        optionsApprovalResponse: IOptionsApprovalResponseModel
    ): IOptionsApprovalRequestModel {
        return {
            id: optionsApprovalResponse.id,
            timeValue: optionsApprovalResponse.timeValue,
            subDepartmentId: optionsApprovalResponse.subDepartmentId,
            attributeValues: optionsApprovalResponse.attributeValues.map(
                (attributeValue: IOptionsAttributeValueResponseModel) => ({
                    id: attributeValue.id,
                    attributeValue: attributeValue.attributeValue,
                    budgets: attributeValue.budgets.map((budget: IBudgetResponseModel) => ({
                        id: budget.id,
                        totalBudget: budget.totalBudget,
                        fashionPercentage: budget.fashionPercentage,
                        corePercentage: budget.corePercentage,
                        productLevelId: budget.productLevelId,
                        productLevelName: budget.productLevelName
                    })),
                    options: attributeValue.options
                        .map((option: IOptionResponseModel) => ({
                            id: option.id,
                            optionsCount: option.optionsCount,
                            productLevelId: option.productLevelId,
                            productLevelName: option.productLevelName,
                            subTimeOptions: option.subTimeOptions.map((subTimeOption: ISubTimeOptionResponseModel) => ({
                                id: subTimeOption.id,
                                optionsCount: subTimeOption.optionsCount,
                                configSubTimeValue: {
                                    id: subTimeOption.configSubTimeValue.id,
                                    displayName: subTimeOption.configSubTimeValue.displayName
                                }
                            }))
                        }))
                        .filter((option: IOptionRequestModel) => option.optionsCount !== 0)
                })
            )
        };
    }

    public async getTableState(state: { allOptions: Array<IOption>; updatedOption: IOption }): Promise<void> {
        this.loading = true;
        this.tyTableState = state.allOptions;

        // 1. Update the TY Options Approval Data with the newly updated option from the
        // monthly options table
        for (const attributeValue of this.tYOptionsApprovalData.attributeValues) {
            if (attributeValue.id === state.updatedOption.attributeValueId) {
                for (const option of attributeValue.options) {
                    if (
                        state.updatedOption.product_level_value === option.productLevelId &&
                        state.updatedOption.optionId === option.id
                    ) {
                        option.optionsCount = state.updatedOption.sku_plan;
                        break;
                    }
                }
            }
        }

        // 2. Call calculate options method to recalculate the values in the TY table
        this.tYOptionsApprovalData = this._mapsCalculationService.calculateOptions(
            this.selectedBrandId,
            this.tYOptionsApprovalData,
            this.lYOptionsApprovalData
        );

        // 3. Use the new Option Approval data to map the Budget, Options, and Monthly Options
        await this.setBudgetAndOptionsData(this.selectedTimeValue);
        await this.setAllMonthlyOptions(this.selectedBrandId);
        await this.updateSingleMonthlyOption(state.updatedOption);

        this.loading = false;
    }

    public async updateSingleMonthlyOption(updatedOption: IOption): Promise<void> {
        this.monthlyOptionsData.rows = this.monthlyOptionsData.rows.map((option: IMonthlyOptionRow) => {
            if (option.product_level_value === updatedOption.product_level_value && option.optionId === updatedOption.optionId) {
                option.sku_plans.sku_plan = updatedOption.sku_plan;
                option.sku_plans.Total = updatedOption.sku_plan;
            }
            return option;
        });
    }

    public async handleMonthlyOptionsSkuUpdate(payload: Array<IOptionsCalculateRowPayload>): Promise<void> {
        this.loading = true;

        try {
            // Update the sub time options of each row to reflect the skus changes in the
            // monthly options table for that specific row
            for (const attributeValue of this.tYOptionsApprovalData.attributeValues) {
                for (const updatedOption of payload) {
                    if (attributeValue.id === updatedOption.attributeValueId) {
                        for (const option of attributeValue.options) {
                            if (
                                updatedOption.product_level_value === option.productLevelId &&
                                updatedOption.optionId === option.id
                            ) {
                                for (const subTimeOption of option.subTimeOptions) {
                                    if (subTimeOption.configSubTimeValue.displayName === updatedOption.sub_time_value) {
                                        subTimeOption.optionsCount = updatedOption.sku_plan;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        } catch (e) {
            this.showErrorMessage(e);
        }
        this.loading = false;
    }

    public async brandChanged(): Promise<void> {
        // Filter departments based on selected brand
        const brandShortId: string = this.getShortId(this.selectedBrandId);
        this.filteredDepartmentValues = this.availableDepartmentValues.filter((department: IConfigProductHierarchyResponseModel): boolean =>
            department.id.includes(brandShortId)
        );

        // Reset dependent fields
        this.selectedDepartmentId = '';
        this.selectedSubDepartmentId = '';
        this.selectedCategoryId = '';

        // Trigger filter change
        await this.filterChanged();
    }
}
